/**
 * Manual sync (v6.0.0) — the Settings "Sync now" button. PURE: no DOM, no
 * fetch, so the orchestration and the wording are unit-tested without a browser.
 *
 * WHAT IT IS (v7.1.0): first ask each connected bank for anything new
 * (`POST /api/sync` — the server's runSync, one bank at a time, each with its
 * own cooldown and its own result), THEN re-fetch everything the screens show
 * from Ballast's own Worker, and ask the browser whether a newer build of the
 * app exists. The order matters: the re-fetch is what shows the bank's news.
 *
 * Until v7.1.0 it never asked a bank, and the Settings copy said so. That copy
 * and e2e/sync.spec.ts's pinned request set changed in the same commit as this.
 *
 * A bank that cannot be asked does NOT fail the sync: the screen is still
 * refreshed from Ballast, and the bank's own line says what happened to it.
 *
 * WHY IT EXISTS: an installed iOS Home Screen app can stay open for days and has
 * no background refresh (see the note on the push handler in sw.ts). Without
 * this, the only way to see current figures was to kill the app.
 */

import { relativeAge } from './freshness-state';

export type SyncPhase = 'idle' | 'syncing' | 'ok' | 'failed';

/** What asking the banks produced: their results, or why they couldn't be asked. */
export type BankAsk<B> = { ok: true; results: B } | { ok: false; cause: unknown };

export type ManualSyncResult<T, B = never> =
  | {
      ok: true;
      data: T;
      updateInstalling: boolean;
      /** Present only when `syncBanks` was supplied. */
      banks?: BankAsk<B>;
    }
  /** `cause` is passed through untouched so the caller can spot a 401. */
  | { ok: false; cause: unknown };

export interface ManualSyncDeps<T, B = never> {
  /**
   * Ask the banks for news (server side). Runs BEFORE `fetchAll`, so the load
   * shows what the banks just said. A rejection here is reported, never fatal.
   */
  syncBanks?: () => Promise<B>;
  /**
   * Load everything the screens show. Must REJECT if any part of it failed to
   * load — a half-loaded result applied to the screen is how a figure goes
   * missing without a word.
   */
  fetchAll: () => Promise<T>;
  /** True when a newer build of the app has started installing. */
  checkForUpdate: () => Promise<boolean>;
}

export async function runManualSync<T, B = never>(
  deps: ManualSyncDeps<T, B>,
): Promise<ManualSyncResult<T, B>> {
  // The update check starts immediately and is best-effort: it must never fail
  // the sync, because being offline to the update server says nothing about the
  // data.
  const update = deps.checkForUpdate().catch(() => false);
  let banks: BankAsk<B> | undefined;
  if (deps.syncBanks) {
    try {
      banks = { ok: true, results: await deps.syncBanks() };
    } catch (cause) {
      banks = { ok: false, cause };
    }
  }
  try {
    const data = await deps.fetchAll();
    return banks
      ? { ok: true, data, updateInstalling: await update, banks }
      : { ok: true, data, updateInstalling: await update };
  } catch (cause) {
    // Let the update check settle so it is not left dangling.
    await update;
    return { ok: false, cause };
  }
}

export interface SyncDescriptionInput {
  phase: SyncPhase;
  /** Epoch ms of the last time the screens' data was successfully loaded. */
  loadedAt: number;
  updateInstalling?: boolean;
  /** Injected for testability; defaults to Date.now. */
  now?: number;
}

export interface SyncDescription {
  text: string;
  busy: boolean;
}

/**
 * The words under the button.
 *
 * A failed sync NEVER says "synced" and always says how old the figures on
 * screen are — silence there is the "green but dead" lie (freshness-state.ts)
 * wearing a different hat. It also says nothing changed, because that is the
 * question the operator has the instant a request fails.
 */
export function describeSync(input: SyncDescriptionInput): SyncDescription {
  const age = relativeAge(Math.max(0, (input.now ?? Date.now()) - input.loadedAt));

  switch (input.phase) {
    case 'syncing':
      return { text: 'Syncing…', busy: true };
    case 'failed':
      return {
        text: `Couldn't reach Ballast. Nothing changed — these figures are from ${age}.`,
        busy: false,
      };
    case 'ok':
    case 'idle':
      return {
        text: input.updateInstalling
          ? `Updated ${age}. A newer version of Ballast is installing — the app will reload.`
          : `Updated ${age}`,
        busy: false,
      };
  }
}
